import { useHits } from "react-instantsearch";
import { useMemo } from "react";
import type {
  HighlightResultOf,
  HitAttributeHighlightResult,
  NormalisedHighlightResultOf,
} from "@/features/search/components/Results/components/Highlight/types";

/**
 * Sorting function to ensure full matches are always prioritised, will
 * otherwise use the most complete match based on the number of matching
 * chars
 */
const byMatchPercentage = (
  a: HitAttributeHighlightResult,
  b: HitAttributeHighlightResult,
) => {
  if (a.matchLevel === "full" && b.matchLevel !== "full") {
    return -1;
  }

  if (a.matchLevel !== "full" && b.matchLevel === "full") {
    return 1;
  }

  return b.matchedWords.join("").length - a.matchedWords.join("").length;
};

// preserve order so their indexes stay aligned
const POSITIONAL_KEYS = new Set(["competitors", "jockeys", "trainers"]);

export const useNormalisedHits = <T>() => {
  const { items, ...rest } = useHits<
    T & {
      key: keyof T;
      _highlightResult?: HighlightResultOf<T>;
    }
  >();
  const normalisedItems = useMemo(
    () =>
      (items || []).map((item) => {
        // highlights can be arrays or objects, we do this to always send an array

        return {
          ...item,
          normalisedHighlights: Object.fromEntries(
            Object.entries(item._highlightResult || {}).map(([key, value]) => {
              return [
                key,
                Array.isArray(value)
                  ? POSITIONAL_KEYS.has(key)
                    ? value
                    : value.sort((a, b) => {
                        return byMatchPercentage(
                          a as HitAttributeHighlightResult,
                          b as HitAttributeHighlightResult,
                        );
                      })
                  : [value],
              ];
            }),
          ) as NormalisedHighlightResultOf<T>,
        };
      }),
    [items],
  );

  return { items: normalisedItems, ...rest };
};
